import type {
  EtlTransportResponse,
  EtlStatusResponse,
  StatusDisplayItem,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  STATUS_PAGE_STATES,
  STATUS_TYPES,
} from '../AirAmbulanceForm/constants';
import type { StatusPageState } from '../types';

/**
 * Extract status display item from an ETL status response.
 */
function extractStatusDisplayItem(
  status: EtlStatusResponse,
): StatusDisplayItem {
  // consider at elevating these to top-level properties in the ETL status
  // response to avoid this nested parsing. For now I the zod-scheams parse and
  // validate it.
  const verbatim = status.verbatim_message;
  const update = verbatim?.update;
  const url = verbatim?.url;

  return {
    type: update?.status?.type,
    display: update?.status?.display ?? undefined,
    timestamp: update?.status?.statusTimeUTC || status.created_at,
    vehicle: update?.vehicle,
    provider: update?.provider,
    trackingUrl: url?.transportTracking ?? undefined,
    eta: update?.eta,
  };
}

/**
 * Sort statuses by timestamp in descending order (newest first).
 */
function sortStatusesByTimestamp(
  statuses: StatusDisplayItem[],
): StatusDisplayItem[] {
  return [...statuses].sort((a, b) => {
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeB - timeA;
  });
}

/**
 * Determine the status page state based on transport data and statuses.
 * Approximates the behavior of getStatusState in statusUtils.ts, adapted for
 * the ETL transport shape (is_test flag instead of launch_type, no timeout concept).
 */
function getStatusPageState(
  transport: EtlTransportResponse | undefined,
  statuses: StatusDisplayItem[],
): StatusPageState {
  // No transport yet: On the status page, we may show it just after the request
  // has been made but ETL hasn't responded. We want to return _something_ here
  // to avoid flickering and show the user that something is happening.
  if (!transport) {
    return STATUS_PAGE_STATES.SUBMITTED;
  }

  // The midas-bvased behavior always shows this test message anytime the
  // transport is a test
  if (transport.is_test) {
    return STATUS_PAGE_STATES.TEST;
  }

  // Statuses are already sorted newest-first; find the first with a valid type
  const latestValidType = statuses.find(s => s.type)?.type;

  if (latestValidType && STATUS_TYPES[latestValidType]) {
    return STATUS_TYPES[latestValidType].pageState;
  }

  // Have statuses but none with a known type — default to ACCEPTED
  if (statuses.length > 0) {
    return STATUS_PAGE_STATES.ACCEPTED;
  }

  return STATUS_PAGE_STATES.SUBMITTED;
}

/**
 * Format raw statuses from a transport into sorted StatusDisplayItems.
 */
function formatTransportStatuses(
  statuses: EtlStatusResponse[] | undefined,
): StatusDisplayItem[] {
  if (!statuses) {
    return [];
  }
  const items = statuses.map(extractStatusDisplayItem);
  return sortStatusesByTimestamp(items);
}

export {
  extractStatusDisplayItem,
  sortStatusesByTimestamp,
  getStatusPageState,
  formatTransportStatuses,
};
